<script setup>
  import { ref, computed, watchEffect, onMounted } from 'vue';
  import { useUserStore } from '@/stores/UserStore'
  import ModalFavoriteItemManager from '@/components/ModalFavoriteItemManager.vue';
  import ModalRenameFavoriteContainer from '@/components/ModalRenameFavoriteContainer.vue';
  import ModalDeleteFavoriteContainer from '@/components/ModalDeleteFavoriteContainer.vue';
  import ModalMoveCopyItemsToProfile from '@/components/ModalMoveCopyItemsToProfile.vue';

  const emits = defineEmits(['close']);

  const props = defineProps({
    containers: {
      type: Array,
      required: true
    },
    favoriteSpotContainerId: {
      type: Number,
      default: null
    },
    show: {
      type: Boolean,
      default: false
    }
  })

  const userStore = useUserStore();

  const containers = ref(props.containers);
  const showModal = ref(false);
  const selectedContainerIds = ref([]);

  const selectedContainers = computed(() => containers.value.filter(container => selectedContainerIds.value.includes(container.container_id)));

  function closeModal() {
    showModal.value = false;

    emits('close');
  }


  async function handleSortEnd() {
    const newContainerOrder = containers.value.map(container => container.container_id);
    newContainerOrder.unshift(props.favoriteSpotContainerId); // Ensure favorite spot container is first in the order
    
    await userStore.sortProfileContainers(userStore.currentProfileId, newContainerOrder);
    await userStore.getProfiles(true);
  }

  onMounted(() => {
    // Placing the watchEffect in the onMounted callback makes sure
    // the BaseModal has time to run the modal open animation by delaying the 
    // showModal value being set to true until after this component mounts    
    watchEffect(() => showModal.value = props.show);
  }); 
  
  watchEffect(() => {
    containers.value = props.containers;
  });
</script>

<template>
  <ModalFavoriteItemManager
    v-if="showModal"
    v-model="selectedContainerIds"
    :items="containers"
    idField="container_id"
    nameField="container_name"
    title="Favorite Containers"
    :show="showModal"
    @sortEnd="handleSortEnd"
    @close="closeModal"
  >
    <template #description>
      <p class="jwx-modal__text">Click on the menu button to rename, copy, move, or delete a favorited container.</p>
      <p class="jwx-modal__text">To manage multiple containers at once, click on the "Select" button. Then select all the containers you would like to manage.</p>
    </template>
    <template #modals="{ showRenameModal, showDeleteModal, showMoveCopyModal, moveCopyAction, closeSecondaryModals }">
      <ModalRenameFavoriteContainer
        v-if="showRenameModal"
        :show="showRenameModal"
        :containerId="editContainerId"
        @close="closeSecondaryModals"
      /> 
      <ModalDeleteFavoriteContainer
        v-if="showDeleteModal"
        :show="showDeleteModal"
        :containerId="selectedContainerIds"
        @close="closeSecondaryModals"
      /> 
      <ModalMoveCopyItemsToProfile
        v-if="showMoveCopyModal"
        :items="selectedContainers"
        type="container"
        :action="moveCopyAction"
        :show="showMoveCopyModal"
        @close="closeSecondaryModals"
      />  
    </template>
  </ModalFavoriteItemManager>
</template>